import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { PageLoader } from './components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './features/auth';

// Lazy load pages for code splitting
const LoginPage = lazy(() =>
  import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);

const DashboardPage = lazy(() =>
  import('./features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);

const VehiclesListPage = lazy(() =>
  import('./features/vehicles/VehiclesListPage').then((m) => ({ default: m.VehiclesListPage })),
);
const VehicleDetailPage = lazy(() =>
  import('./features/vehicles/VehicleDetailPage').then((m) => ({ default: m.VehicleDetailPage })),
);
const VehiclePhotosPage = lazy(() =>
  import('./features/vehicles/VehiclePhotosPage').then((m) => ({ default: m.VehiclePhotosPage })),
);

const KVAListPage = lazy(() =>
  import('./features/kva/KVAListPage').then((m) => ({ default: m.KVAListPage })),
);
const KVADetailPage = lazy(() =>
  import('./features/kva/KVADetailPage').then((m) => ({ default: m.KVADetailPage })),
);

const InvoicesListPage = lazy(() =>
  import('./features/invoices/InvoicesListPage').then((m) => ({ default: m.InvoicesListPage })),
);
const InvoiceDetailPage = lazy(() =>
  import('./features/invoices/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })),
);

const DamageListPage = lazy(() =>
  import('./features/damage').then((m) => ({ default: m.DamageListPage })),
);
const DamageDetailPage = lazy(() =>
  import('./features/damage').then((m) => ({ default: m.DamageDetailPage })),
);

const RepairListPage = lazy(() =>
  import('./features/repair').then((m) => ({ default: m.RepairListPage })),
);
const RepairDetailPage = lazy(() =>
  import('./features/repair').then((m) => ({ default: m.RepairDetailPage })),
);

const FleetSignPage = lazy(() =>
  import('./features/fleet-sign/FleetSignPage').then((m) => ({ default: m.FleetSignPage })),
);

const SettingsPage = lazy(() =>
  import('./features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

// PRODUCT GAP: Users-management feature disabled — GET /users returns 403 for
// CUSTOMER_ADMIN (users controller excludes customer roles) and there is no
// customer-scoped user endpoint. Re-enable once a customer-scoped users API exists.
// const UsersListPage = lazy(() =>
//   import('./features/users').then((m) => ({ default: m.UsersListPage })),
// );
// const UserDetailPage = lazy(() =>
//   import('./features/users').then((m) => ({ default: m.UserDetailPage })),
// );

// Not Found page
function NotFound() {
  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
        <p className="text-gray-600 mb-6">Seite nicht gefunden</p>
        <a
          href="/"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Zur Startseite
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/fleet-sign/:token" element={<FleetSignPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route index element={<DashboardPage />} />

            {/* Vehicles */}
            <Route path="vehicles" element={<VehiclesListPage />} />
            <Route path="vehicles/:id" element={<VehicleDetailPage />} />
            <Route path="vehicles/:id/photos" element={<VehiclePhotosPage />} />

            {/* KVAs */}
            <Route path="kvas" element={<KVAListPage />} />
            <Route path="kvas/:id" element={<KVADetailPage />} />

            {/* Invoices */}
            <Route path="invoices" element={<InvoicesListPage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />

            {/* Damage Cases */}
            <Route path="damage" element={<DamageListPage />} />
            <Route path="damage/:id" element={<DamageDetailPage />} />

            {/* Repair Orders */}
            <Route path="repair" element={<RepairListPage />} />
            <Route path="repair/:id" element={<RepairDetailPage />} />

            {/* Settings */}
            <Route path="settings" element={<SettingsPage />} />

            {/* Users Management — disabled: no customer-scoped users API (GET /users → 403). */}
            {/* <Route path="users" element={<UsersListPage />} /> */}
            {/* <Route path="users/:id" element={<UserDetailPage />} /> */}

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
